import { useState, useEffect, useCallback } from 'react';
import supabase from '../supabase/supabase';

const PARTNER_FIELDS = 'id, name, slug, genre, avatar_image, cover_image, verified';

export const DEFAULT_TERMS = {
  payment_schedule: 'monthly',
  exclusivity: 'non_exclusive',
  territory: 'worldwide',
  content_rights: 'shared',
  cancellation_notice_days: 30,
  auto_renew: false,
  custom_clauses: [],
};

export const DEFAULT_MILESTONE = {
  title: '', description: '', target_date: '',
  priority: 'medium', category: 'other', notes: '', sort_order: 0,
};

/* ─── Notification helpers ─────────────────────────────────────────── */
const notify = async (userId, type, title, message, metadata = {}, extra = {}) => {
  if (!userId) return;
  await supabase.from('d2f_notifications').insert({
    user_id: userId, type, title, message, metadata,
    priority: extra.priority || 'normal',
    category: extra.category || 'general',
    action_url: extra.actionUrl || '',
  });
};

const resolveUserId = async (artistId) => {
  if (!artistId) return null;
  const { data } = await supabase.from('d2f_artists').select('user_id').eq('id', artistId).single();
  return data?.user_id || null;
};

/* ─── Hook ─────────────────────────────────────────────────────────── */
export const usePartnerships = (artistId, { onToast } = {}) => {
  const [partnerships, setPartnerships] = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState(null);

  const fetchPartnerships = useCallback(async () => {
    if (!artistId) { setLoading(false); return; }
    setLoading(true); setError(null);
    try {
      const [initRes, partRes] = await Promise.all([
        supabase
          .from('d2f_partnerships')
          .select(`*, partner:partner_artist_id(${PARTNER_FIELDS}), milestones:d2f_partnership_milestones(*)`)
          .eq('initiator_artist_id', artistId)
          .order('created_at', { ascending: false }),
        supabase
          .from('d2f_partnerships')
          .select(`*, initiator:initiator_artist_id(${PARTNER_FIELDS}), milestones:d2f_partnership_milestones(*)`)
          .eq('partner_artist_id', artistId)
          .order('created_at', { ascending: false }),
      ]);
      const initiated = (initRes.data || []).map(p => ({
        ...p, _role: 'initiator', _peer: p.partner,
        milestones: (p.milestones || []).sort((a, b) => a.sort_order - b.sort_order),
      }));
      const received = (partRes.data || []).map(p => ({
        ...p, _role: 'partner', _peer: p.initiator,
        milestones: (p.milestones || []).sort((a, b) => a.sort_order - b.sort_order),
      }));
      setPartnerships(
        [...initiated, ...received].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      );
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [artistId]);

  useEffect(() => { fetchPartnerships(); }, [fetchPartnerships]);

  /* Real-time */
  useEffect(() => {
    if (!artistId) return;
    const ch = supabase
      .channel(`partnerships:${artistId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'd2f_partnerships', filter: `partner_artist_id=eq.${artistId}` }, fetchPartnerships)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'd2f_partnerships', filter: `initiator_artist_id=eq.${artistId}` }, fetchPartnerships)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'd2f_partnership_milestones' }, fetchPartnerships)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [artistId, fetchPartnerships]);

  /* ── Create ─────────────────────────────────────────────────────── */
  const createPartnership = async (payload) => {
    const { data, error } = await supabase
      .from('d2f_partnerships')
      .insert({ ...payload, terms_structured: payload.terms_structured || DEFAULT_TERMS, terms_version: 1 })
      .select().single();
    if (!error && data) {
      await fetchPartnerships();
      const partnerUserId = await resolveUserId(data.partner_artist_id);
      const initiatorName = payload._initiatorName || 'An artist';
      if (partnerUserId) {
        await notify(partnerUserId, 'partnership_request',
          '🤝 New Partnership Request',
          `${initiatorName} wants to partner with you on "${data.title}".`,
          { partnershipId: data.id, partnershipType: data.partnership_type },
          { priority: 'high', category: 'partnership' }
        );
      }
      onToast?.({ type: 'partnership_request', title: 'Request Sent',
        message: `Your partnership request for "${data.title}" has been sent.`,
        metadata: { partnershipId: data.id } });
    }
    return { data, error };
  };

  /* ── Update status ──────────────────────────────────────────────── */
  const updateStatus = async (id, status) => {
    const { data: p } = await supabase
      .from('d2f_partnerships')
      .select('*, initiator:initiator_artist_id(name, user_id), partner:partner_artist_id(name, user_id)')
      .eq('id', id).single();
    const { error } = await supabase
      .from('d2f_partnerships')
      .update({ status, updated_at: new Date().toISOString() }).eq('id', id);
    if (!error && p) {
      await fetchPartnerships();
      const { initiator, partner, title } = p;
      if (status === 'active') {
        await notify(initiator?.user_id, 'partnership_accepted',
          '🎉 Partnership Accepted!',
          `${partner?.name} accepted your request for "${title}". You're now partners!`,
          { partnershipId: id },
          { priority: 'high', category: 'partnership' }
        );
        onToast?.({ type: 'partnership_accepted', title: 'Partnership Accepted!',
          message: `${partner?.name} accepted "${title}".`, metadata: { partnershipId: id } });
      }
      if (status === 'declined') {
        await notify(initiator?.user_id, 'partnership_declined',
          'Partnership Declined',
          `${partner?.name} declined your request for "${title}".`,
          { partnershipId: id },
          { priority: 'normal', category: 'partnership' }
        );
        onToast?.({ type: 'partnership_declined', title: 'Partnership Declined',
          message: `"${title}" was declined.`, metadata: { partnershipId: id } });
      }
      if (status === 'ended') {
        await notify(partner?.user_id, 'partnership_ended',
          'Partnership Ended',
          `${initiator?.name} ended the partnership "${title}".`,
          { partnershipId: id },
          { priority: 'normal', category: 'partnership' }
        );
        onToast?.({ type: 'partnership_ended', title: 'Partnership Ended',
          message: `"${title}" has been ended.`, metadata: { partnershipId: id } });
      }
    }
    return { error };
  };

  /* ── Update terms ───────────────────────────────────────────────── */
  const updateTerms = async (id, terms_structured, terms_free, revSplit) => {
    const { data: current } = await supabase
      .from('d2f_partnerships')
      .select('terms_version, title, partner_artist_id')
      .eq('id', id).single();
    const nextVersion = (current?.terms_version || 1) + 1;
    const { error } = await supabase
      .from('d2f_partnerships')
      .update({ terms_structured, terms: terms_free || '', revenue_split: revSplit ?? undefined, terms_version: nextVersion, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error && current) {
      await fetchPartnerships();
      const partnerUserId = await resolveUserId(current.partner_artist_id);
      await notify(partnerUserId, 'partnership_terms_updated',
        '📄 Terms Updated',
        `Terms for "${current.title}" updated to v${nextVersion}. Please review.`,
        { partnershipId: id, version: nextVersion },
        { priority: 'high', category: 'partnership' }
      );
      onToast?.({ type: 'partnership_terms_updated', title: 'Terms Saved',
        message: `Terms updated to v${nextVersion}. Partner notified.`, metadata: { partnershipId: id } });
    }
    return { error };
  };

  /* ── Agree to terms ─────────────────────────────────────────────── */
  const agreeToTerms = async (id, userId) => {
    const { data: p } = await supabase
      .from('d2f_partnerships')
      .select('title, initiator_artist_id, partner:partner_artist_id(name)')
      .eq('id', id).single();
    const { error } = await supabase
      .from('d2f_partnerships')
      .update({ terms_agreed_at: new Date().toISOString(), terms_agreed_by: userId, status: 'active', updated_at: new Date().toISOString() })
      .eq('id', id);
    if (!error && p) {
      await fetchPartnerships();
      const initiatorUserId = await resolveUserId(p.initiator_artist_id);
      await notify(initiatorUserId, 'partnership_accepted',
        '🎉 Terms Agreed & Active!',
        `${p.partner?.name} agreed to terms. "${p.title}" is now active!`,
        { partnershipId: id },
        { priority: 'urgent', category: 'partnership' }
      );
      onToast?.({ type: 'partnership_accepted', title: 'Terms Agreed & Active!',
        message: `"${p.title}" is now an active partnership.`, metadata: { partnershipId: id } });
    }
    return { error };
  };

  /* ── Add milestone ──────────────────────────────────────────────── */
  const addMilestone = async (partnershipId, milestoneData) => {
    const { data: existing } = await supabase
      .from('d2f_partnership_milestones')
      .select('sort_order')
      .eq('partnership_id', partnershipId)
      .order('sort_order', { ascending: false })
      .limit(1);
    const nextOrder = (existing?.[0]?.sort_order ?? -1) + 1;
    const { data, error } = await supabase
      .from('d2f_partnership_milestones')
      .insert({ ...milestoneData, partnership_id: partnershipId, sort_order: nextOrder })
      .select().single();
    if (!error && data) {
      await fetchPartnerships();
      const { data: p } = await supabase
        .from('d2f_partnerships')
        .select('title, initiator_artist_id, partner_artist_id')
        .eq('id', partnershipId).single();
      if (p) {
        const [iUid, pUid] = await Promise.all([
          resolveUserId(p.initiator_artist_id),
          resolveUserId(p.partner_artist_id),
        ]);
        const notifyBoth = (uid) => notify(uid, 'partnership_milestone_added',
          '🎯 New Milestone Added',
          `"${milestoneData.title}" was added to "${p.title}".`,
          { milestoneId: data.id, partnershipId },
          { priority: 'normal', category: 'milestone' }
        );
        await Promise.all([notifyBoth(iUid), notifyBoth(pUid)]);
      }
      onToast?.({ type: 'partnership_milestone_added', title: 'Milestone Added',
        message: `"${milestoneData.title}" added to the partnership.`,
        metadata: { partnershipId } });
    }
    return { data, error };
  };

  /* ── Update milestone ───────────────────────────────────────────── */
  const updateMilestone = async (milestoneId, updates) => {
    const { data: prev } = await supabase
      .from('d2f_partnership_milestones')
      .select('title, status, progress_pct, partnership_id')
      .eq('id', milestoneId).single();

    const { error } = await supabase
      .from('d2f_partnership_milestones')
      .update(updates).eq('id', milestoneId);

    if (!error && prev) {
      await fetchPartnerships();
      const { data: p } = await supabase
        .from('d2f_partnerships')
        .select('title, initiator_artist_id, partner_artist_id')
        .eq('id', prev.partnership_id).single();

      if (p) {
        const [iUid, pUid] = await Promise.all([
          resolveUserId(p.initiator_artist_id),
          resolveUserId(p.partner_artist_id),
        ]);
        const notifyBoth = (uid, type, title, msg, extra) =>
          notify(uid, type, title, msg, { milestoneId, partnershipId: prev.partnership_id }, extra);

        // Status changed
        if (updates.status && updates.status !== prev.status) {
          const statusLabel = updates.status.replace('_', ' ');
          await Promise.all([iUid, pUid].map(uid =>
            notifyBoth(uid, 'milestone_status_change',
              `📌 Milestone Status: ${statusLabel}`,
              `"${prev.title}" moved to ${statusLabel} in "${p.title}".`,
              { priority: 'normal', category: 'milestone' }
            )
          ));
        }
        // Progress updated (only notify on meaningful jumps: 25, 50, 75, 100)
        if (updates.progress_pct !== undefined && updates.progress_pct !== prev.progress_pct) {
          const milestones = [25, 50, 75, 100];
          const hit = milestones.find(m => updates.progress_pct >= m && (prev.progress_pct || 0) < m);
          if (hit) {
            await Promise.all([iUid, pUid].map(uid =>
              notifyBoth(uid, 'milestone_progress',
                `📊 Milestone ${hit}% Complete`,
                `"${prev.title}" is now ${hit}% done in "${p.title}".`,
                { priority: hit === 100 ? 'high' : 'low', category: 'milestone' }
              )
            ));
          }
        }
      }
    }
    return { error };
  };

  /* ── Delete milestone ───────────────────────────────────────────── */
  const deleteMilestone = async (milestoneId) => {
    const { error } = await supabase
      .from('d2f_partnership_milestones')
      .delete().eq('id', milestoneId);
    if (!error) await fetchPartnerships();
    return { error };
  };

  /* ── Toggle milestone ───────────────────────────────────────────── */
  const toggleMilestone = async (milestoneId, completed) => {
    const { data: ms } = await supabase
      .from('d2f_partnership_milestones')
      .select('title, partnership_id').eq('id', milestoneId).single();
    const { error } = await supabase
      .from('d2f_partnership_milestones')
      .update({ completed, completed_at: completed ? new Date().toISOString() : null, status: completed ? 'done' : 'in_progress' })
      .eq('id', milestoneId);
    if (!error && completed && ms) {
      await fetchPartnerships();
      const { data: p } = await supabase
        .from('d2f_partnerships')
        .select('title, initiator_artist_id, partner_artist_id')
        .eq('id', ms.partnership_id).single();
      if (p) {
        const [iUid, pUid] = await Promise.all([
          resolveUserId(p.initiator_artist_id),
          resolveUserId(p.partner_artist_id),
        ]);
        await Promise.all([iUid, pUid].map(uid =>
          notify(uid, 'milestone_completed',
            '✅ Milestone Completed!',
            `"${ms.title}" was completed in "${p.title}". Great work!`,
            { milestoneId, partnershipId: ms.partnership_id },
            { priority: 'high', category: 'milestone' }
          )
        ));
        onToast?.({ type: 'milestone_completed', title: 'Milestone Complete!',
          message: `"${ms.title}" has been marked complete.`,
          metadata: { milestoneId, partnershipId: ms.partnership_id } });
      }
    } else if (!error) {
      await fetchPartnerships();
    }
    return { error };
  };

  return {
    partnerships, loading, error, refetch: fetchPartnerships,
    createPartnership, updateStatus, updateTerms, agreeToTerms,
    addMilestone, updateMilestone, deleteMilestone, toggleMilestone,
  };
};